/**
 * DecisionCard — handles BOTH platform agent_decisions (_type="platform")
 * and legal_tickets (_type="legal").
 *
 * Props:
 *   decision   — the decision or legal ticket object
 *   onAction   — (decision, action, extra) callback
 *   isFocused  — boolean (j/k keyboard nav active state)
 */
import { useState, useCallback, useRef } from "react";
import {
  Coins,
  ShieldCheck,
  FileText,
  Scale,
  ChevronDown,
  ChevronRight,
  Clock,
  Clipboard,
  BookOpen,
  Loader2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders() {
  const token = getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Priority derivation ───────────────────────────────────────────────────────

const STAKES_RANK = { high: 1, medium: 2, low: 3 };
const URGENCY_RANK = { urgent: 1, high: 1, normal: 2, low: 3 };

function derivePlatformPriority(decision) {
  const stakesHigh = decision.stakes === "high";
  const urgencyHigh = decision.urgency === "high" || decision.urgency === "urgent";
  const ageMs = decision.surfaced_at
    ? Date.now() - new Date(decision.surfaced_at).getTime()
    : 0;
  const ageOver30m = ageMs > 30 * 60 * 1000;

  if (stakesHigh && (urgencyHigh || ageOver30m)) return "P1";
  if (stakesHigh || (decision.stakes === "medium" && urgencyHigh)) return "P2";
  if (decision.stakes === "medium") return "P3";
  return "P4";
}

function deriveLegalPriority(ticket) {
  const map = { critical: "P1", high: "P2", medium: "P3", low: "P4" };
  return map[ticket.priority] || "P3";
}

function derivePriority(decision) {
  if (decision._type === "legal") return deriveLegalPriority(decision);
  return derivePlatformPriority(decision);
}

// ── Age formatter ─────────────────────────────────────────────────────────────

function formatAge(isoString) {
  if (!isoString) return "—";
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// ── SLA countdown ─────────────────────────────────────────────────────────────

function formatSla(isoString) {
  if (!isoString) return null;
  const ms = new Date(isoString).getTime() - Date.now();
  if (ms <= 0) return { label: "OVERDUE", variant: "overdue" };
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days <= 1) return { label: `${days}d left`, variant: "critical" };
  if (days <= 6) return { label: `${days}d left`, variant: "amber" };
  return { label: `${days}d left`, variant: "ok" };
}

// ── Type icon ─────────────────────────────────────────────────────────────────

function resolveTypeIcon(decision) {
  const title = (
    decision.context?.title ||
    decision.recommendation ||
    decision.subject ||
    ""
  ).toLowerCase();

  if (/payment|payout|pay/.test(title)) return Coins;
  if (/vet|verify|badge|shield/.test(title)) return ShieldCheck;
  if (/refund/.test(title)) return FileText; // ReceiptRefund not in lucide-react stable, use FileText
  if (decision._type === "legal") return Scale;
  return FileText;
}

// ── Redact sensitive keys from JSON ──────────────────────────────────────────

const SENSITIVE = /password|token|secret|key|credential/i;

function redactJson(obj) {
  if (!obj || typeof obj !== "object") return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => {
      if (SENSITIVE.test(k)) return [k, "***redacted***"];
      if (typeof v === "object" && v !== null) return [k, redactJson(v)];
      return [k, v];
    })
  );
}

// ── ExpandableSection ─────────────────────────────────────────────────────────

function ExpandableSection({ label, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mc-dc-expandable">
      <button
        className="mc-dc-expandable-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown size={12} aria-hidden="true" />
        ) : (
          <ChevronRight size={12} aria-hidden="true" />
        )}
        <span>{label}</span>
      </button>
      {open && <div className="mc-dc-expandable-body">{children}</div>}
    </div>
  );
}

// ── SnoozeDropdown ────────────────────────────────────────────────────────────

const SNOOZE_OPTIONS = [
  { label: "30 min", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "4 hours", minutes: 240 },
  { label: "Tomorrow morning", minutes: 60 * 14 }, // ~14h from midnight-ish
];

function SnoozeDropdown({ onSnooze }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  const handleBlur = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.relatedTarget)) {
      setOpen(false);
    }
  }, []);

  return (
    <div
      className="mc-dc-snooze-wrap"
      ref={ref}
      onBlur={handleBlur}
    >
      <button
        className="mc-dc-btn mc-dc-btn--ghost"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Clock size={12} aria-hidden="true" />
        Snooze
        <ChevronDown size={10} aria-hidden="true" />
      </button>
      {open && (
        <ul className="mc-dc-snooze-menu" role="listbox">
          {SNOOZE_OPTIONS.map((opt) => (
            <li key={opt.minutes}>
              <button
                className="mc-dc-snooze-option"
                role="option"
                onClick={() => {
                  setOpen(false);
                  onSnooze(opt.minutes);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── AskClaude panel ───────────────────────────────────────────────────────────

function AskClaudePanel({ decisionId }) {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const res = await axios.post(
        `${API}/admin/ops/ask-claude`,
        { decision_id: decisionId, question: question.trim() },
        { headers: authHeaders() }
      );
      setResponse(res.data?.answer || res.data?.response || JSON.stringify(res.data));
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Claude request failed");
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return (
      <button
        className="mc-dc-btn mc-dc-btn--ghost"
        onClick={() => setOpen(true)}
      >
        <BookOpen size={12} aria-hidden="true" />
        Ask Claude
      </button>
    );
  }

  return (
    <div className="mc-dc-ask-claude">
      <form onSubmit={handleSubmit} className="mc-dc-ask-form">
        <textarea
          className="mc-dc-ask-textarea"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask Claude about this decision…"
          rows={2}
          disabled={loading}
          autoFocus
        />
        <div className="mc-dc-ask-actions">
          <button
            type="submit"
            className="mc-dc-btn mc-dc-btn--primary"
            disabled={loading || !question.trim()}
          >
            {loading ? (
              <>
                <Loader2 size={11} className="mc-dc-spin" aria-hidden="true" />
                Asking Claude…
              </>
            ) : (
              "Ask"
            )}
          </button>
          <button
            type="button"
            className="mc-dc-btn mc-dc-btn--ghost"
            onClick={() => {
              setOpen(false);
              setQuestion("");
              setResponse(null);
              setError(null);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
      {error && (
        <p className="mc-dc-ask-error" role="alert">
          {error}
        </p>
      )}
      {response && (
        <div className="mc-dc-ask-response" aria-live="polite">
          <span className="mc-dc-ask-response-label">Claude:</span>
          <p className="mc-dc-ask-response-text">{response}</p>
        </div>
      )}
    </div>
  );
}

// ── RejectWithReason ──────────────────────────────────────────────────────────

function RejectWithReason({ onSubmit }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  if (!open) {
    return (
      <button
        className="mc-dc-btn mc-dc-btn--outline"
        onClick={() => setOpen(true)}
      >
        ✕ Reject + reason
      </button>
    );
  }

  return (
    <div className="mc-dc-reject-reason">
      <textarea
        className="mc-dc-ask-textarea"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason for rejection…"
        rows={2}
        autoFocus
      />
      <div className="mc-dc-ask-actions">
        <button
          className="mc-dc-btn mc-dc-btn--critical"
          disabled={!reason.trim()}
          onClick={() => onSubmit(reason.trim())}
        >
          Reject
        </button>
        <button
          className="mc-dc-btn mc-dc-btn--ghost"
          onClick={() => {
            setOpen(false);
            setReason("");
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── DecisionCard ──────────────────────────────────────────────────────────────

export default function DecisionCard({ decision, onAction, isFocused }) {
  const isLegal = decision._type === "legal";
  const priority = derivePriority(decision);
  const isHighPriority = priority === "P1" || priority === "P2";

  const TypeIcon = resolveTypeIcon(decision);

  // Title
  const title = isLegal
    ? decision.subject
    : decision.context?.title ||
      `${decision.recommendation || "Decision"} · ${decision.agent_id || ""}`;

  // Correlation ID
  const corrId = decision.correlation_id || decision.id || "";
  const corrIdShort = corrId.slice(0, 6);

  // SLA (legal only)
  const sla = isLegal ? formatSla(decision.sla_due_at) : null;

  // Age
  const age = formatAge(decision.surfaced_at || decision.created_at);

  // Metadata line
  const metaAgent = decision.agent_id
    ? `Agent: ${decision.agent_id}`
    : isLegal
    ? `Category: ${decision.category || "—"}`
    : null;
  const metaConf =
    !isLegal && decision.confidence != null
      ? `Confidence: ${decision.confidence}`
      : null;

  // Subline
  const subline = isLegal
    ? null
    : decision.context?.subject
    ? `Subject: ${decision.context.subject}`
    : null;

  function handleAction(action, extra) {
    if (onAction) onAction(decision, action, extra);
  }

  // Copy correlation ID
  function copyCorrelationId() {
    navigator.clipboard
      .writeText(corrId)
      .then(() => toast("Copied"))
      .catch(() => toast.error("Copy failed"));
  }

  return (
    <article
      className={[
        "mc-dc",
        isFocused ? "mc-dc--focused" : "",
        isHighPriority ? "mc-dc--high-priority" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={`Decision: ${title}`}
    >
      {/* ── Header row ── */}
      <div className="mc-dc-header">
        <div className="mc-dc-header-left">
          {/* Priority tag */}
          <span
            className={[
              "mc-dc-priority",
              isHighPriority ? "mc-dc-priority--critical" : "mc-dc-priority--muted",
            ].join(" ")}
            aria-label={`Priority ${priority}`}
          >
            {priority}
          </span>

          {/* Legal badge */}
          {isLegal && (
            <span className="mc-dc-legal-badge" aria-label="Legal ticket">
              ⚖ LEGAL
            </span>
          )}

          {/* Age */}
          <span className="mc-dc-age mc-mono" aria-label={`Age: ${age}`}>
            {age}
          </span>

          {/* Type icon */}
          <span className="mc-dc-type-icon" aria-hidden="true">
            <TypeIcon size={14} />
          </span>

          {/* SLA countdown (legal) */}
          {sla && (
            <span
              className={[
                "mc-dc-sla",
                `mc-dc-sla--${sla.variant}`,
              ].join(" ")}
              aria-label={`SLA: ${sla.label}`}
            >
              {sla.label}
            </span>
          )}

          {/* Category chip (legal) */}
          {isLegal && decision.category && (
            <span className="mc-dc-category-chip">
              {decision.category}
            </span>
          )}
        </div>

        <div className="mc-dc-header-right">
          {/* Title */}
          <span className="mc-dc-title">{title}</span>

          {/* Correlation ID */}
          {corrIdShort && (
            <button
              className="mc-dc-corr-id mc-mono"
              onClick={copyCorrelationId}
              title={`Copy full ID: ${corrId}`}
              aria-label={`Correlation ID: ${corrIdShort}. Click to copy.`}
            >
              {corrIdShort}
              <Clipboard size={10} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* ── Meta row ── */}
      {(metaAgent || metaConf) && (
        <div className="mc-dc-meta mc-mono">
          {metaAgent && <span>{metaAgent}</span>}
          {metaAgent && metaConf && (
            <span className="mc-dc-meta-sep" aria-hidden="true"> · </span>
          )}
          {metaConf && <span>{metaConf}</span>}
        </div>
      )}

      {/* ── Subline ── */}
      {subline && <div className="mc-dc-subline">{subline}</div>}

      {/* ── Expandable sections ── */}
      <div className="mc-dc-expandables">
        {/* Context */}
        <ExpandableSection label="Context">
          <pre className="mc-dc-json mc-mono">
            {JSON.stringify(
              redactJson(isLegal ? decision.payload : decision.context),
              null,
              2
            )}
          </pre>
        </ExpandableSection>

        {/* Alternatives (platform only) */}
        {!isLegal && (decision.options || []).length > 0 && (
          <ExpandableSection label="Alternatives">
            <ul className="mc-dc-options-list">
              {decision.options.map((opt, i) => (
                <li key={i} className="mc-dc-option-item">
                  <span className="mc-dc-option-label">
                    {typeof opt === "string" ? opt : opt.label || `Option ${i + 1}`}
                  </span>
                  {opt.rationale && (
                    <span className="mc-dc-option-rationale">
                      {opt.rationale}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </ExpandableSection>
        )}

        {/* Audit trail */}
        <ExpandableSection label="Audit trail">
          {isLegal ? (
            <div className="mc-dc-audit">
              {decision.draft_response && (
                <div className="mc-dc-audit-section">
                  <span className="mc-dc-audit-label">Agent draft:</span>
                  <p className="mc-dc-audit-text">{decision.draft_response}</p>
                </div>
              )}
              {(decision.evidence_uris || []).length > 0 && (
                <div className="mc-dc-audit-section">
                  <span className="mc-dc-audit-label">Evidence:</span>
                  <ul className="mc-dc-evidence-list">
                    {decision.evidence_uris.map((uri, i) => (
                      <li key={i} className="mc-mono" style={{ fontSize: 11 }}>
                        {uri}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="mc-dc-audit">
              <p className="mc-dc-audit-text">
                Audit trail available via correlation_id:{" "}
                <span className="mc-mono mc-dc-audit-corr">{corrId || "—"}</span>
              </p>
              <p className="mc-dc-audit-note">
                Full action log is linked from the Actions room.
              </p>
            </div>
          )}
        </ExpandableSection>

        {/* Agent draft (legal) */}
        {isLegal && decision.draft_response && (
          <ExpandableSection label="Agent draft">
            <p className="mc-dc-audit-text" style={{ whiteSpace: "pre-wrap" }}>
              {decision.draft_response}
            </p>
          </ExpandableSection>
        )}
      </div>

      {/* ── Action row ── */}
      <div className="mc-dc-actions">
        {isLegal ? (
          /* Legal actions */
          <>
            <button
              className="mc-dc-btn mc-dc-btn--primary"
              onClick={() =>
                handleAction("legal_decide", { action: "approve" })
              }
            >
              Approve draft
            </button>
            <button
              className="mc-dc-btn mc-dc-btn--outline"
              onClick={() =>
                handleAction("legal_decide", { action: "revise" })
              }
            >
              Request revision
            </button>
            <button
              className="mc-dc-btn mc-dc-btn--critical"
              onClick={() =>
                handleAction("legal_decide", { action: "reject" })
              }
            >
              Reject
            </button>
          </>
        ) : (
          /* Platform actions */
          <>
            {/* Primary — approve/recommend */}
            <button
              className="mc-dc-btn mc-dc-btn--primary"
              onClick={() =>
                handleAction("decide", {
                  decision: decision.recommendation || "A",
                })
              }
            >
              {decision.recommendation || "Approve"}
            </button>

            {/* Counter */}
            <button
              className="mc-dc-btn mc-dc-btn--outline"
              onClick={() => handleAction("decide", { decision: "B" })}
            >
              {decision.recommendation === "Hold" ? "Approve" : "Hold"}
            </button>

            {/* Reject with reason */}
            <RejectWithReason
              onSubmit={(notes) =>
                handleAction("decide", { decision: "reject", notes })
              }
            />

            {/* Snooze dropdown */}
            <SnoozeDropdown
              onSnooze={(minutes) => handleAction("snooze", { minutes })}
            />

            {/* Ask Claude */}
            <AskClaudePanel decisionId={decision.id} />
          </>
        )}
      </div>

      {/* ── Inline card styles ── */}
      <style>{`
        .mc-dc {
          background-color: var(--mc-surface);
          border: 1px solid var(--mc-border);
          border-left: 2px solid transparent;
          border-radius: var(--radius-md);
          padding: 14px 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          transition:
            border-color var(--dur, 150ms) var(--ease, ease),
            background-color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-dc--focused {
          border-left-color: var(--color-green);
          background-color: rgba(23, 128, 61, 0.04);
        }

        .mc-dc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          flex-wrap: wrap;
        }

        .mc-dc-header-left {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .mc-dc-header-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          min-width: 0;
          justify-content: flex-end;
        }

        .mc-dc-priority {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.10em;
          text-transform: uppercase;
          font-family: var(--font-mono);
          flex-shrink: 0;
        }

        .mc-dc-priority--critical {
          color: var(--color-critical);
        }

        .mc-dc-priority--muted {
          color: var(--color-slate-400);
        }

        .mc-dc-legal-badge {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          color: var(--color-green);
          font-family: var(--font-sans);
          background-color: var(--color-green-pale, rgba(23,128,61,0.10));
          border: 1px solid rgba(23,128,61,0.30);
          border-radius: 3px;
          padding: 1px 5px;
          flex-shrink: 0;
        }

        .mc-dc-age {
          font-size: 10px;
          color: var(--color-slate-500);
          flex-shrink: 0;
        }

        .mc-dc-type-icon {
          color: var(--color-slate-500);
          display: inline-flex;
          align-items: center;
          flex-shrink: 0;
        }

        .mc-dc-sla {
          font-size: 10px;
          font-family: var(--font-mono);
          font-weight: 600;
          flex-shrink: 0;
          padding: 1px 5px;
          border-radius: 3px;
        }

        .mc-dc-sla--ok {
          color: var(--color-slate-400);
        }

        .mc-dc-sla--amber {
          color: #C45E28;
          background-color: rgba(224, 122, 78, 0.12);
        }

        .mc-dc-sla--critical {
          color: var(--color-critical);
          background-color: var(--color-critical-bg, rgba(140,31,18,0.10));
        }

        @keyframes mc-sla-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }

        .mc-dc-sla--overdue {
          color: var(--color-critical);
          background-color: var(--color-critical-bg, rgba(140,31,18,0.10));
          animation: mc-sla-blink 1.2s ease-in-out infinite;
        }

        .mc-dc-category-chip {
          font-size: 10px;
          font-family: var(--font-mono);
          color: var(--color-slate-400);
          background-color: var(--mc-bg, var(--color-ink));
          border: 1px solid var(--mc-border);
          border-radius: 3px;
          padding: 1px 5px;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          flex-shrink: 0;
        }

        .mc-dc-title {
          font-size: 15px;
          font-weight: 500;
          font-family: var(--font-sans);
          color: var(--mc-text);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
          min-width: 0;
          text-align: right;
        }

        .mc-dc-corr-id {
          font-size: 10px;
          color: var(--color-slate-500);
          background: transparent;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 0;
          flex-shrink: 0;
          transition: color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-dc-corr-id:hover {
          color: var(--mc-text);
        }

        .mc-dc-meta {
          font-size: 10px;
          color: var(--color-slate-500);
          display: flex;
          align-items: center;
          gap: 0;
        }

        .mc-dc-meta-sep {
          color: var(--mc-border);
        }

        .mc-dc-subline {
          font-size: 11px;
          color: var(--color-slate-500);
          font-family: var(--font-sans);
        }

        .mc-dc-expandables {
          display: flex;
          flex-direction: column;
          gap: 0;
          border-top: 1px solid var(--mc-border);
          margin-top: 4px;
          padding-top: 4px;
        }

        .mc-dc-expandable {
          display: flex;
          flex-direction: column;
        }

        .mc-dc-expandable-trigger {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          font-family: var(--font-sans);
          color: var(--color-slate-500);
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 5px 0;
          text-align: left;
          transition: color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-dc-expandable-trigger:hover {
          color: var(--mc-text);
        }

        .mc-dc-expandable-body {
          padding: 4px 0 8px 16px;
        }

        .mc-dc-json {
          font-size: 10px;
          line-height: 1.5;
          color: var(--color-slate-400);
          background-color: var(--mc-bg, var(--color-ink));
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md);
          padding: 8px 10px;
          overflow-x: auto;
          max-height: 200px;
          overflow-y: auto;
          margin: 0;
          white-space: pre;
        }

        .mc-dc-options-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .mc-dc-option-item {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .mc-dc-option-label {
          font-size: 12px;
          font-family: var(--font-sans);
          color: var(--mc-text);
          font-weight: 500;
        }

        .mc-dc-option-rationale {
          font-size: 11px;
          color: var(--color-slate-500);
          font-family: var(--font-sans);
        }

        .mc-dc-audit {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .mc-dc-audit-section {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .mc-dc-audit-label {
          font-size: 10px;
          font-family: var(--font-sans);
          color: var(--color-slate-500);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }

        .mc-dc-audit-text {
          font-size: 11px;
          color: var(--color-slate-400);
          font-family: var(--font-sans);
          margin: 0;
          line-height: 1.5;
        }

        .mc-dc-audit-corr {
          color: var(--color-green);
        }

        .mc-dc-audit-note {
          font-size: 10px;
          color: var(--color-slate-600);
          font-family: var(--font-sans);
          margin: 0;
        }

        .mc-dc-evidence-list {
          list-style: none;
          padding: 0;
          margin: 4px 0 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
          color: var(--color-slate-400);
        }

        /* ── Actions ── */

        .mc-dc-actions {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 6px;
          padding-top: 8px;
          border-top: 1px solid var(--mc-border);
        }

        .mc-dc-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px 11px;
          border-radius: var(--radius-md, 6px);
          font-size: 12px;
          font-family: var(--font-sans);
          font-weight: 500;
          cursor: pointer;
          border: 1px solid transparent;
          transition:
            background-color var(--dur, 150ms) var(--ease, ease),
            border-color var(--dur, 150ms) var(--ease, ease),
            color var(--dur, 150ms) var(--ease, ease);
          white-space: nowrap;
        }

        .mc-dc-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .mc-dc-btn--primary {
          background-color: var(--color-green);
          color: var(--color-paper, #FAF8F4);
          border-color: var(--color-green);
        }

        .mc-dc-btn--primary:hover:not(:disabled) {
          background-color: #135f30;
          border-color: #135f30;
        }

        .mc-dc-btn--outline {
          background: transparent;
          color: var(--mc-text);
          border-color: var(--mc-border);
        }

        .mc-dc-btn--outline:hover:not(:disabled) {
          border-color: var(--mc-text);
        }

        .mc-dc-btn--ghost {
          background: transparent;
          color: var(--color-slate-400);
          border-color: transparent;
        }

        .mc-dc-btn--ghost:hover:not(:disabled) {
          color: var(--mc-text);
        }

        .mc-dc-btn--critical {
          background: transparent;
          color: var(--color-critical);
          border-color: var(--color-critical);
        }

        .mc-dc-btn--critical:hover:not(:disabled) {
          background-color: var(--color-critical-bg, rgba(140,31,18,0.10));
        }

        /* Snooze dropdown */

        .mc-dc-snooze-wrap {
          position: relative;
          display: inline-flex;
        }

        .mc-dc-snooze-menu {
          position: absolute;
          bottom: calc(100% + 4px);
          left: 0;
          background-color: var(--mc-surface);
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md, 6px);
          padding: 4px 0;
          margin: 0;
          list-style: none;
          z-index: 50;
          min-width: 148px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
        }

        .mc-dc-snooze-option {
          width: 100%;
          text-align: left;
          padding: 6px 12px;
          background: transparent;
          border: none;
          font-size: 12px;
          font-family: var(--font-sans);
          color: var(--mc-text);
          cursor: pointer;
          transition: background-color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-dc-snooze-option:hover {
          background-color: rgba(23, 128, 61, 0.08);
          color: var(--color-green);
        }

        /* Ask Claude panel */

        .mc-dc-ask-claude,
        .mc-dc-reject-reason {
          display: flex;
          flex-direction: column;
          gap: 6px;
          width: 100%;
          padding: 8px;
          background-color: var(--mc-bg, var(--color-ink));
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md, 6px);
          margin-top: 4px;
        }

        .mc-dc-ask-form {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .mc-dc-ask-textarea {
          width: 100%;
          padding: 7px 10px;
          border: 1px solid var(--mc-border);
          border-radius: var(--radius-md, 6px);
          font-size: 12px;
          font-family: var(--font-sans);
          color: var(--mc-text);
          background-color: var(--mc-surface);
          resize: vertical;
          box-sizing: border-box;
          outline: none;
          transition: border-color var(--dur, 150ms) var(--ease, ease);
        }

        .mc-dc-ask-textarea:focus {
          border-color: var(--color-green);
        }

        .mc-dc-ask-actions {
          display: flex;
          gap: 6px;
          align-items: center;
        }

        .mc-dc-ask-error {
          font-size: 11px;
          color: var(--color-critical);
          font-family: var(--font-sans);
          margin: 0;
        }

        .mc-dc-ask-response {
          border-top: 1px solid var(--mc-border);
          padding-top: 8px;
          display: flex;
          flex-direction: column;
          gap: 3px;
        }

        .mc-dc-ask-response-label {
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--color-green);
          font-family: var(--font-sans);
        }

        .mc-dc-ask-response-text {
          font-size: 12px;
          color: var(--mc-text);
          font-family: var(--font-sans);
          margin: 0;
          line-height: 1.5;
          white-space: pre-wrap;
        }

        @keyframes mc-spin {
          to { transform: rotate(360deg); }
        }

        .mc-dc-spin {
          animation: mc-spin 0.8s linear infinite;
        }
      `}</style>
    </article>
  );
}
